<script setup lang="ts">
import { UniqueComponentId } from 'primevue/utils';
import { inject, ref, useAttrs } from 'vue';

import { ButtonSplitProps } from './ButtonSplit.vue.d';
import Button from '../button/Button.vue';
import Menu from '../menu/Menu.vue';
import MenuInstance from '../menu/Menu.vue.d';

const props = defineProps<ButtonSplitProps>();

const emit = defineEmits<{
  click: [event: Event];
}>();

const attrs = useAttrs();

const Preset = inject<Record<string, any>>('preset', {}).buttonsplit ?? {};

const id = ref(attrs['id'] || UniqueComponentId());
const menu = ref<MenuInstance>(null);

const onDropdownButtonClick = (event: Event): void => {
  if (event) {
    event.preventDefault();
  }

  menu.value.toggle(event);
};

const onDropdownKeydown = (event: KeyboardEvent): void => {
  if (event.code === 'ArrowDown' || event.code === 'ArrowUp') {
    onDropdownButtonClick(event);
    event.preventDefault();
  }
};

const onDefaultButtonClick = (event: Event): void => {
  emit('click', event);
};
</script>

<template>
  <div
    :class="[...Preset?.root, props.class]"
    :data-p-severity="severity"
    :style="style"
    @click="onDropdownButtonClick"
  >
    <Button
      v-bind="props.buttonProps"
      :aria-label="label"
      :class="Preset?.button.class"
      :disabled="disabled"
      :icon="icon"
      :label="label"
      :outlined="outlined"
      :pt="Preset?.button"
      :severity="severity"
      :size="size"
      :text="text"
      :unstyled="unstyled"
      @click.stop="onDefaultButtonClick"
      class=""
      type="button"
    />

    <Button
      :aria-controls="id + '_overlay'"
      :class="Preset?.menuButton"
      :disabled="disabled"
      v-bind="props.menuButtonProps"
      :icon="menuButtonIcon ?? 'arrow-down-fill'"
      :outlined="outlined"
      :pt="Preset?.menuButton"
      :severity="severity"
      :size="size"
      :text="text"
      :unstyled="unstyled"
      @keydown="onDropdownKeydown"
      aria-haspopup="true"
      type="button"
    />

    <Menu
      :id="id + '_overlay'"
      ref="menu"
      :append-to="appendTo"
      :auto-z-index="autoZIndex"
      :base-z-index="baseZIndex"
      :model="model"
      :pt="Preset?.menu"
      :unstyled="unstyled"
      popup
    >
      <template #itemicon="slotProps" v-if="$slots.menuitemicon">
        <slot
          :class="slotProps.class"
          :item="slotProps.item"
          name="menuitemicon"
        />
      </template>

      <template #item="slotProps" v-if="$slots.item">
        <slot
          :has-submenu="slotProps.hasSubmenu"
          :item="slotProps.item"
          :label="slotProps.label"
          :props="slotProps.props"
          name="item"
        />
      </template>
    </Menu>
  </div>
</template>
